import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthserviceService, MainNavigationComponent } from '@ace/ace';
import { GradingComponent } from './features/assessments/grading/grading.component';
import { ExamCorrectionsDetailComponent } from './features/exams/exam-corrections-detail.component';
import { MyExamCorrectionsComponent } from './features/exams/my-exam-corrections.component';
import { PlatformFeatureGuard } from './core/guard/platform-feature.guard';

const routes: Routes = [
  {
    path: 'login',
    loadChildren: () =>
      import('@ace/ace').then(m => m.LoginModule),
  },
  {
    path: 'forgot-password',
    loadChildren: () =>
      import('@ace/ace').then(m => m.ForgotPasswordModule),
  },
  {
    path: 'reset-password',
    loadChildren: () =>
      import('@ace/ace').then(m => m.ResetPasswordModule),
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  {
    path: 'main',
    component: MainNavigationComponent,
    canActivate: [AuthserviceService],
    children: [
      {
        path: 'user-management',
        loadChildren: () =>
          import('@ace/ace').then(
            (m) => m.UserManagementModule
          ),
      },
      {
        path: 'master-management-v2',
        loadChildren: () =>
          import(
            '@ace/ace'
          ).then((m) => m.MasterManagementV2Module),
      },
      {
        path: 'landing-page',
        loadChildren: () =>
          import('./features/dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'teaching',
        loadChildren: () =>
          import('./features/teaching/teaching.module').then(m => m.TeachingModule)
      },
      {
        path: 'courses',
        loadChildren: () =>
          import('./features/courses/courses.module').then(m => m.CoursesModule)
      },
      {
        path: 'recommendations',
        loadChildren: () =>
          import('./features/recommendations/recommendations.module').then(m => m.RecommendationsModule)
      },
      {
        path: 'learners',
        loadChildren: () =>
          import('./features/learners/learners.module').then(m => m.LearnersModule)
      },
      {
        path: 'learner-management',
        loadChildren: () =>
          import('./features/learners-feedback/learners-feedback.module').then(m => m.LearnersFeedbackModule)
      },
      {
        path: 'question-library',
        canActivate: [PlatformFeatureGuard],
        data: { platformFeature: 'assessment' },
        loadChildren: () =>
          import('./features/question-bank/question-bank.module').then(m => m.QuestionBankModule)
      },
      {
        path: 'quizzes',
        canActivate: [PlatformFeatureGuard],
        data: { platformFeature: 'assessment' },
        loadChildren: () =>
          import('./features/quizzes/quizzes.module').then(m => m.QuizzesModule)
      },
      {
        path: 'quiz-corrections',
        redirectTo: 'assessment/marking',
        pathMatch: 'full'
      },
      {
        path: 'quiz-corrections/:id',
        redirectTo: 'assessment/marking/:id',
        pathMatch: 'full'
      },
      {
        path: 'quiz-corrections/:id/submissions/:resultId',
        redirectTo: 'assessment/marking/:id/submissions/:resultId',
        pathMatch: 'full'
      },
      {
        path: 'exams',
        canActivate: [PlatformFeatureGuard],
        data: { platformFeature: 'assessment' },
        loadChildren: () =>
          import('./features/exams/exams.module').then(m => m.ExamsModule)
      },
      {
        path: 'assessment/marking',
        canActivate: [PlatformFeatureGuard],
        data: { platformFeature: 'assessment' },
        component: MyExamCorrectionsComponent
      },
      {
        path: 'assessment/marking/:id',
        canActivate: [PlatformFeatureGuard],
        data: { platformFeature: 'assessment' },
        component: ExamCorrectionsDetailComponent
      },
      {
        path: 'assessment/marking/:id/submissions/:resultId',
        canActivate: [PlatformFeatureGuard],
        data: { platformFeature: 'assessment' },
        component: GradingComponent
      },
      {
        path: 'exam-corrections',
        redirectTo: 'assessment/marking',
        pathMatch: 'full'
      },
      {
        path: 'exam-corrections/:id',
        redirectTo: 'assessment/marking/:id',
        pathMatch: 'full'
      },
      {
        path: 'exam-corrections/:id/submissions/:resultId',
        redirectTo: 'assessment/marking/:id/submissions/:resultId',
        pathMatch: 'full'
      },
      {
        path: 'homework',
        canActivate: [PlatformFeatureGuard],
        data: { platformFeature: 'assessment' },
        loadChildren: () =>
          import('./features/homework/homework.module').then(m => m.HomeworkModule)
      },
      {
        path: 'homework-corrections',
        redirectTo: 'assessment/marking',
        pathMatch: 'full'
      },
      {
        path: 'homework-corrections/:id',
        redirectTo: 'assessment/marking/:id',
        pathMatch: 'full'
      },
      {
        path: 'homework-corrections/:id/submissions/:resultId',
        redirectTo: 'assessment/marking/:id/submissions/:resultId',
        pathMatch: 'full'
      },
      {
        path: 'events',
        loadChildren: () =>
          import('./features/events/events.module').then(m => m.EventsModule)
      },
      {
        path: 'messaging',
        loadChildren: () =>
          import('./features/messaging/messaging.module').then(m => m.MessagingModule)
      },
      {
        path: 'copilot-studio',
        canActivate: [PlatformFeatureGuard],
        data: { platformFeature: 'aiAuthoring' },
        loadChildren: () =>
          import('./features/copilot-studio/copilot-studio.module').then(m => m.CopilotStudioModule)
      },
      {
        path: 'mentions',
        loadChildren: () =>
          import('./features/mentions/mentions.module').then(m => m.MentionsModule)
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./features/profile/profile.module').then(m => m.ProfileModule)
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./features/settings/settings.module').then(m => m.SettingsModule)
      },
      {
        path: 'program-management',
        loadChildren: () =>
          import('./features/program-management/program-management.module').then(m => m.ProgramManagementModule)
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' }
    ]
  },
  { path: '**', redirectTo: 'login', pathMatch: 'full' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
